import React, { useState, useEffect } from 'react';
import { updateCropAddField } from '../firebase/firestoreService';
import { useLocation, useNavigate } from 'react-router-dom';


// List of crop names
const cropsName = [
  "PADDY", "CHOLAM", "CUMBU", "RAGI", "MAIZE", "RED GRAM", 
  "BENGAL GRAM", "GREEN GRAM", "BLACK GRAM", "HORSE GRAM", 
  "GROUNDNUT", "GINGELLY", "POTATO", "ONION", "SUGARCANE", "COTTON"
];

// Styling
const cropAddStyle = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
    fontFamily: "'Poppins', sans-serif",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backdropFilter: "blur(0px)",
  },
  formContainer: {
    background: "rgba(255, 255, 255, 0.3)", 
    backdropFilter: "blur(10px)", 
    padding: "15px 30px",
    borderRadius: "15px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
    textAlign: "center",
    maxWidth: "420px",
    width: "80%",
    border: "2px solid #f1f8e9",
  },
  h3: {
    fontFamily: "'Roboto Slab', serif",
    fontSize: "1.6rem",
    padding: "10px",
    color: "#FFFFFF",
    fontWeight: "bold",
    textShadow: "1px 1px 3px rgba(0, 0, 0, 0.4)",
  },
  label: {
    fontSize: "14px",
    color: "#FFFFFF",
    marginBottom: "6px",
    display: "block",
    textAlign: "left",
  },
  inputWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "15px",
  },
  input: {
    width: "60%",
    padding: "10px",
    marginLeft: "10px",
    borderRadius: "8px",
    border: "1px solid #FFFFFF",
    fontSize: "14px",
    backgroundColor: "#F1F8E9",
    boxSizing: "border-box",
    transition: "all 0.3s ease",
    outline: "none",
    color: "#333",
  },
  inputFocus: {
    borderColor: "#66bb6a",
    boxShadow: "0 0 6px rgba(102, 187, 106, 0.6)",
  },
  button: {
    width: "100%",
    padding: "12px",
    margin: "10px 0",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: "#66bb6a",
    color: "white",
    fontWeight: "bold",
    transition: "background-color 0.3s ease, transform 0.3s ease, box-shadow 0.2s ease",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  },
  buttonHover: {
    backgroundColor: "#f1f8e9",
    color: "#66bb6a", 
    transform: "scale(1.0)",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.3)",
  },
  errorText: {
    color: "red",
    fontSize: "12px",
    marginTop: "8px",
  },
};

// CropAddComponent
const CropAddComponent = ( { email_1 } ) => {
  const location = useLocation(); // Get location from the router
  let { email, cropName } = location.state || {};  // Extract email and cropName from state
  
  if ( !cropName ) email = email_1;

  const [selectedCrop, setSelectedCrop] = useState(cropName || ''); // Default to passed cropname or empty
  const [fieldSize, setFieldSize] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputFocus, setInputFocus] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);

  const navigate = useNavigate();

  const handleCropNameChange = (event) => {
    setSelectedCrop(event.target.value);
  };

  const handleFieldSizeChange = (event) => {
    const value = event.target.value;
    setFieldSize(value === '' ? value : parseFloat(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!selectedCrop || !fieldSize || isNaN(fieldSize) || fieldSize <= 0) {
      setError('Please select a crop and enter a valid field size (positive number).');
      return;
    }

    try {
      setLoading(true);
      const fieldSizeFloat = parseFloat(fieldSize);
      if (isNaN(fieldSizeFloat)) {
        setError('Please enter a valid number for the field size.');
        return;
      }

      await updateCropAddField(email, selectedCrop, fieldSizeFloat );

      alert('Crop and field size added successfully!!!!');
      if (window.history.length > 2) {
        navigate(-1); // Go back to the previous page
      } else {
        navigate('/dashboard'); // No history, go to dashboard
      }

    } catch (error) {
      console.error('Error adding crop and field size: ', error);
      setError('Failed to add crop details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={cropAddStyle.container}>
      <div style={cropAddStyle.formContainer}>
        <h3 style={cropAddStyle.h3}>Add Crop and Field Size</h3>

        {error && <p style={cropAddStyle.errorText}>{error}</p>}

        <form onSubmit={handleSubmit}>
          {/* Crop Name: Display as editable or non-editable */}
          <div style={cropAddStyle.inputWrapper}>
            <label style={cropAddStyle.label} htmlFor="cropName">Select Crop:</label>
            {cropName ? (
              // If cropname is passed, show it as non-editable
              <input
                type="text"
                id="cropName"
                value={selectedCrop}
                disabled
                style={cropAddStyle.input}
              />
            ) : (
              // If cropname is not passed, show a dropdown
              <select
                id="cropName"
                value={selectedCrop}
                onChange={handleCropNameChange}
                style={cropAddStyle.input}
              >
                <option value="">Select a crop</option>
                {cropsName.map((crop, index) => (
                  <option key={index} value={crop}>
                    {crop}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Field Size */}
          <div style={cropAddStyle.inputWrapper}>
            <label style={cropAddStyle.label} htmlFor="fieldSize">Field Size (in hectare):</label>
            <input
              type="number"
              id="fieldSize"
              value={fieldSize}
              onChange={handleFieldSizeChange}
              style={inputFocus ? { ...cropAddStyle.input, ...cropAddStyle.inputFocus } : cropAddStyle.input}
              min="0"
              step="0.01"
              onFocus={() => setInputFocus(true)}
              onBlur={() => setInputFocus(false)}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={buttonHover ? { ...cropAddStyle.button, ...cropAddStyle.buttonHover } : cropAddStyle.button}
            onMouseEnter={() => setButtonHover(true)}
            onMouseLeave={() => setButtonHover(false)}
          >
            {loading ? 'Adding...' : 'Add Crop Details'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CropAddComponent;
